<template>
  <div class="tablet-calculate">
    <b-card>
      <!-- 日期選擇區 -->
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="開始日期">
            <flat-pickr
              v-model="dateRange.startDate"
              class="form-control"
              :config="{ dateFormat: 'Y-m-d' }"
              @on-change="handleDateChange"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="結束日期">
            <flat-pickr
              v-model="dateRange.endDate"
              class="form-control"
              :config="{ dateFormat: 'Y-m-d' }"
              @on-change="handleDateChange"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- 數據表格 -->
      <div class="table-container">
        <b-table
          :items="tableData"
          :fields="fields"
          responsive
          show-empty
          striped
          class="mt-2"
          sticky-header="500px"
        >
          <template #cell(MaterialType)="data">
            <span :class="['status-circle', getColorClass(data.value)]"></span>
          </template>
          
          <template #cell(ExpectedDate)="data">
            {{ formatDate(data.value) }}
          </template>
          
          <template #cell(Keep_KickOff)="data">
            {{ formatDateTime(data.value) }}
          </template>
          
          <template #cell(KickOff)="data">
            {{ formatDateTime(data.value) }}
          </template>
          
          <template #cell(DeadLine)="data">
            {{ formatDateTime(data.value) }}
          </template>

          <template #cell(ProduceMsg)="data">
            <span v-html="formatProduceMsg(data.value)"></span>
          </template>

          <template #empty>
            <div class="text-center">無數據</div>
          </template>
        </b-table>
      </div>

      <!-- 載入中提示 -->
      <div v-if="loading" class="text-center mt-2">
        <b-spinner label="Loading..."></b-spinner>
      </div>

      <!-- 錯誤提示 -->
      <b-alert
        v-if="error"
        variant="danger"
        show
        class="mt-2"
      >
        {{ error }}
      </b-alert>
    </b-card>
  </div>
</template>

<script>
import { BCard, BTable, BRow, BCol, BFormGroup, BSpinner, BAlert } from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import tools from '@/sag-core/tools'

export default {
  name: 'SagTabletCalculate',

  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BSpinner,
    BAlert,
    flatPickr
  },

  props: {
    value: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      dateRange: {
        startDate: '2024-11-01',
        endDate: '2024-11-30'
      },
      fields: [
        { key: 'MaterialType', label: '狀態' },
        { key: 'LotNum', label: '製令單號' },
        { key: 'ProduceQty', label: '預計產量' },
        { key: 'StdUPH', label: 'UPH' },
        { key: 'ProduceHour', label: '生產時數' },
        { key: 'MF006', label: '線別' },
        { key: 'StoreHouse', label: '入庫線別' },
        { key: 'ProduceMsg', label: '生產品號' },
        { key: 'Roller', label: 'RollerNo' },
        { key: 'ProcessDrawing', label: '各站圖面' },
        { key: 'WorkDrawing', label: '工程圖檔' },
        { key: 'WorkBSA', label: '工程BSA' },
        { key: 'Packaging', label: '包裝規範' },
        { key: 'ExpectedDate', label: 'APS第一次預計交期' },
        { key: 'Keep_KickOff', label: 'APS第一次預計開工' },
        { key: 'KickOff', label: 'APS預計開工' },
        { key: 'DeadLine', label: 'APS預計完工' },
        { key: 'ProcessFlow', label: '製程加工順序' }
      ]
    }
  },

  computed: {
    tableData() {
      return this.value || []
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.handleDateChange()
    })
  },

  methods: {
    handleDateChange() {
      tools.debug.log({
        tag: 'DEBUG',
        title: 'Date Range Changed',
        content: this.dateRange
      })
      
      this.$emit('date-change', {
        startDate: this.dateRange.startDate,
        endDate: this.dateRange.endDate
      })
    },

    getColorClass(type) {
      const colorMap = {
        green: 'text-success',
        yellow: 'text-warning',
        red: 'text-danger'
      }
      return colorMap[type.toLowerCase()] || ''
    },

    formatDate(dateString) {
      if (!dateString) return ''
      return new Date(dateString).toLocaleDateString('zh-TW')
    },

    formatDateTime(dateString) {
      if (!dateString) return ''
      return new Date(dateString).toLocaleString('zh-TW')
    },

    formatDateToString(date) {
      return date.toISOString().split('T')[0]
    },

    formatProduceMsg(msg) {
      if (!msg) return ''
      return msg.split('\r\n').join('<br>')
    }
  }
}
</script>

<style scoped>
.tablet-calculate {
  width: 100%;
}

.table-container {
  width: 100%;
  overflow-x: auto;
  margin-bottom: 1rem;
}

::v-deep .table {
  min-width: 2000px; /* 確保表格有足夠的最小寬度以顯示所有列 */
}

::v-deep .table th {
  white-space: nowrap;
  background-color: #f8f9fa;
}

::v-deep .table td {
  white-space: nowrap;
  padding: 0.5rem;
}

.status-circle {
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 50%;
}

.status-circle.text-success {
  background-color: #28a745;
}

.status-circle.text-warning {
  background-color: #ffc107;
}

.status-circle.text-danger {
  background-color: #dc3545;
}
</style>
